import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch, fetchSignedImageUrl, refresh, useSignedImageUrl } from '../api';
import { CurvePoints, drawCurveOverlay } from './curveOverlay';
import { AnnotationBox, Annotations, EditResult, addBoxes, deleteBox, deleteRange, exchangeRange, interpolateRange, reassignRange, setPlayerId, toLabelCsv } from './labelEdits';

interface ImageViewerProps {
    isOpen: boolean;
    onClose: () => void;
    gcsPrefix: string;
    annotationPath?: string;
    labelPath?: string;
    cameraType?: string;
    dataType?: string;
}

type Selection = { playerId?: number; frame?: number; index?: number };

type Point = { x: number; y: number };

interface ImageCacheAuth {
    prefix: string;
    expires: number;
    signature: string;
}

const IMAGE_CACHE_URL = 'https://training-images.bepro.ai/image';

const getFrameNumberFromPath = (path: string): number | null => {
    const name = path.split('/').pop()?.split('.')[0];
    const match = name?.match(/(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
};

const buildCachedImageUrl = (path: string, auth: ImageCacheAuth) => {
    return `${IMAGE_CACHE_URL}?${new URLSearchParams({
        gcs_path: path,
        prefix: auth.prefix,
        expires: String(auth.expires),
        signature: auth.signature,
    })}`;
};

const segmentColor = (id: number): string => {
    const hue = (id * 137.508) % 360;
    return `hsl(${hue}, 80%, 55%)`;
};

const catmullRomToPath = (pts: Point[], w: number, h: number): string => {
    if (pts.length < 2) return '';
    const px = (p: Point) => (p.x / w) * 100;
    const py = (p: Point) => (p.y / h) * 100;
    if (pts.length === 2) return `M${px(pts[0])} ${py(pts[0])} L${px(pts[1])} ${py(pts[1])}`;

    const extended = [pts[0], ...pts, pts[pts.length - 1]];
    let d = `M${px(pts[0])} ${py(pts[0])}`;
    for (let i = 1; i < extended.length - 2; i++) {
        const p0 = extended[i - 1], p1 = extended[i], p2 = extended[i + 1], p3 = extended[i + 2];
        const cp1x = px(p1) + (px(p2) - px(p0)) / 6;
        const cp1y = py(p1) + (py(p2) - py(p0)) / 6;
        const cp2x = px(p2) - (px(p3) - px(p1)) / 6;
        const cp2y = py(p2) - (py(p3) - py(p1)) / 6;
        d += ` C${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${px(p2)} ${py(p2)}`;
    }
    return d;
};

const fitEllipse = (pts: Point[], w: number, h: number): { cx: number; cy: number; rx: number; ry: number; angle: number } | null => {
    if (pts.length < 5) return null;
    const ps = pts.map(p => ({ x: (p.x / w) * 100, y: (p.y / h) * 100 }));
    const N = 5;
    const MtM = Array.from({ length: N }, () => Array(N).fill(0));
    const Mtb = Array(N).fill(0);
    for (const p of ps) {
        const row = [p.x * p.x, p.x * p.y, p.y * p.y, p.x, p.y];
        for (let i = 0; i < N; i++) {
            Mtb[i] += row[i];
            for (let j = 0; j < N; j++) MtM[i][j] += row[i] * row[j];
        }
    }
    const aug = MtM.map((r, i) => [...r, Mtb[i]]);
    for (let col = 0; col < N; col++) {
        let mr = col;
        for (let r = col + 1; r < N; r++) if (Math.abs(aug[r][col]) > Math.abs(aug[mr][col])) mr = r;
        [aug[col], aug[mr]] = [aug[mr], aug[col]];
        if (Math.abs(aug[col][col]) < 1e-12) return null;
        for (let r = col + 1; r < N; r++) {
            const f = aug[r][col] / aug[col][col];
            for (let j = col; j <= N; j++) aug[r][j] -= f * aug[col][j];
        }
    }
    const coeff = Array(N).fill(0);
    for (let i = N - 1; i >= 0; i--) {
        coeff[i] = aug[i][N];
        for (let j = i + 1; j < N; j++) coeff[i] -= aug[i][j] * coeff[j];
        coeff[i] /= aug[i][i];
    }
    const [a, b, c, d, e] = coeff;
    const det = 4 * a * c - b * b;
    if (det <= 0) return null;
    const cx = (b * e - 2 * c * d) / det;
    const cy = (b * d - 2 * a * e) / det;
    const g = a * cx * cx + b * cx * cy + c * cy * cy + d * cx + e * cy - 1;
    const angle = Math.atan2(b, a - c) / 2;
    const ct = Math.cos(angle), st = Math.sin(angle);
    const a2 = a * ct * ct + b * ct * st + c * st * st;
    const c2 = a * st * st - b * ct * st + c * ct * ct;
    const rx2 = -g / a2, ry2 = -g / c2;
    if (rx2 <= 0 || ry2 <= 0) return null;
    return { cx, cy, rx: Math.sqrt(rx2), ry: Math.sqrt(ry2), angle: angle * 180 / Math.PI };
};

const ImageViewer: React.FC<ImageViewerProps> = ({ isOpen, onClose, gcsPrefix, annotationPath, labelPath, cameraType, dataType }) => {
    const [images, setImages] = useState<string[]>([]);
    const [loadingList, setLoadingList] = useState<boolean>(false);
    const [loadingImage, setLoadingImage] = useState<boolean>(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [cacheAuth, setCacheAuth] = useState<ImageCacheAuth | null>(null);
    const [originalMode, setOriginalMode] = useState<boolean>(false);
    
    const [annotations, setAnnotations] = useState<Map<number, AnnotationBox[]>>(new Map());
    const [isNormalized, setIsNormalized] = useState<boolean>(false);
    const [loadingAnnotations, setLoadingAnnotations] = useState<boolean>(false);

    // Edit mode: a selection follows its playerId across frames; boxes without a playerId are selected by frame + index
    const [editMode, setEditMode] = useState<boolean>(false);
    const [selected, setSelected] = useState<Selection | null>(null);
    const [dirty, setDirty] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [targetId, setTargetId] = useState<string>('');
    const [rangeStart, setRangeStart] = useState<string>('');
    const [rangeEnd, setRangeEnd] = useState<string>('');
    const [editStatus, setEditStatus] = useState<string>('');
    // Draw mode: drag a box. Single adds it to this frame; Range keeps the START box as pending (not in annotations) until the END box fills the range
    const [drawMode, setDrawMode] = useState<boolean>(false);
    const [drawObjectId, setDrawObjectId] = useState<number>(0);
    const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
    const [drawRange, setDrawRange] = useState<boolean>(false);
    const [drawArmed, setDrawArmed] = useState<boolean>(false);
    const [pending, setPending] = useState<AnnotationBox | null>(null);
    // Undo / redo: snapshots of annotations before each edit (edits never mutate a Map, so references are enough)
    const [undoStack, setUndoStack] = useState<Annotations[]>([]);
    const [redoStack, setRedoStack] = useState<Annotations[]>([]);

    const [fieldSegments, setFieldSegments] = useState<Map<number, Map<number, Point[]>>>(new Map());
    const [segmentTypes, setSegmentTypes] = useState<Record<string, string>>({});

    // Curve overlay for panorama curves datasets (static homography, same for all frames)
    const [curvePoints, setCurvePoints] = useState<CurvePoints | null>(null);
    const [inputPoints, setInputPoints] = useState<Point[]>([]);
    const curveCanvasRef = useRef<HTMLCanvasElement>(null);

    // Scaling state
    const [scale, setScale] = useState<number>(1);
    const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
    const [sourceDimensions, setSourceDimensions] = useState<{ width: number; height: number } | null>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [areaSize, setAreaSize] = useState<{ width: number; height: number } | null>(null);
    const [zoom, setZoom] = useState({ z: 1, x: 0, y: 0 });
    const zoomRef = useRef(zoom);
    zoomRef.current = zoom;
    const panRef = useRef<{ cx: number; cy: number; x: number; y: number; moved: boolean } | null>(null);
    const suppressClickRef = useRef(false);

    // Jump to input state
    const [jumpInput, setJumpInput] = useState<string>('');

    // Reels playback state
    const [reelsMode, setReelsMode] = useState<boolean>(false);
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [fps, setFps] = useState<number>(5);
    const [loop, setLoop] = useState<boolean>(true);
    const [bufferedIndices, setBufferedIndices] = useState<Set<number>>(new Set());
    const bufferedIndicesRef = useRef<Set<number>>(new Set());
    const preloadedRef = useRef<Map<number, HTMLImageElement>>(new Map());
    const playTimerRef = useRef<number | null>(null);

    // Use ref to focus the modal for keyboard events
    const modalRef = useRef<HTMLDivElement>(null);

    const BUFFER_AHEAD = 120;
    const BUFFER_BEHIND = 30;
    const BUFFER_CONCURRENCY = 18;
    const PRELOAD_STEP = 60;

    const resetReels = () => {
        if (playTimerRef.current !== null) {
            clearInterval(playTimerRef.current);
            playTimerRef.current = null;
        }
        preloadedRef.current.forEach(img => {
            img.onload = null;
            img.onerror = null;
            img.src = '';
        });
        preloadedRef.current.clear();
        bufferedIndicesRef.current = new Set();
        setBufferedIndices(new Set());
        setIsPlaying(false);
    };

    useEffect(() => {
        if (isOpen && gcsPrefix) {
            setImages([]);
            setCacheAuth(null);
            setImageDimensions(null);
            setSourceDimensions(null);
            resetReels();
            fetchImages();
            setSelectedImageIndex(0);
            setJumpInput('1');
            setOriginalMode(false);
            setEditMode(false);
            setSelected(null);
            setDirty(false);
            setEditStatus('');
            setUndoStack([]);
            setRedoStack([]);
            setDrawMode(false);
            setDrawArmed(false);
            setPending(null);
            setZoom({ z: 1, x: 0, y: 0 });
        }
    }, [isOpen, gcsPrefix]);

    useEffect(() => {
        setSelected(prev => (prev && prev.playerId === undefined ? null : prev));
    }, [selectedImageIndex]);

    useEffect(() => {
        if (!dirty) return;
        const warn = (e: BeforeUnloadEvent) => e.preventDefault();
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [dirty]);

    useEffect(() => {
        if (!isOpen || images.length === 0) return;

        const probe = new Image();
        probe.onload = () => setSourceDimensions({ width: probe.naturalWidth, height: probe.naturalHeight });
        let cancelled = false;
        fetchSignedImageUrl(images[0]).then((url) => {
            if (!cancelled) probe.src = url;
        });
        return () => {
            cancelled = true;
            probe.onload = null;
            probe.src = '';
        };
    }, [isOpen, images]);

    // Reset preload state when reels mode is turned off or viewer closes
    useEffect(() => {
        if (!reelsMode || !isOpen) {
            resetReels();
        }
    }, [reelsMode, isOpen]);
    
    useEffect(() => {
        if (isOpen && labelPath) {
            fetchLabel();
        } else if (isOpen && annotationPath) {
            fetchAnnotations();
            setIsNormalized(false);
        } else {
            setAnnotations(new Map());
            setIsNormalized(false);
        }
    }, [isOpen, annotationPath, labelPath]);

    useEffect(() => {
        if (isOpen && gcsPrefix && cameraType === 'non_panorama') {
            fetchFieldKeypoints();
            fetchSegmentTypes();
        } else {
            setFieldSegments(new Map());
            setSegmentTypes({});
        }
    }, [isOpen, gcsPrefix, cameraType]);

    useEffect(() => {
        if (isOpen && gcsPrefix && dataType === 'curves' && cameraType && cameraType !== 'non_panorama') {
            fetchCurveOverlay();
        } else {
            setCurvePoints(null);
            setInputPoints([]);
        }
    }, [isOpen, gcsPrefix, cameraType, dataType]);

    useEffect(() => {
        if (isOpen && modalRef.current) {
            modalRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && images.length > 0) {
            if (!(reelsMode && bufferedIndices.has(selectedImageIndex))) {
                setLoadingImage(true);
                setScale(1);
            }
            setJumpInput((selectedImageIndex + 1).toString());
        }
    }, [selectedImageIndex, images, isOpen, originalMode, cacheAuth]);

    useEffect(() => {
        bufferedIndicesRef.current = bufferedIndices;
    }, [bufferedIndices]);

    const preloadWindow = Math.floor(selectedImageIndex / PRELOAD_STEP);

    // Reels: preload a sliding window of frames into the browser cache
    useEffect(() => {
        if (!isOpen || !reelsMode || !cacheAuth || images.length === 0) return;

        const anchor = preloadWindow * PRELOAD_STEP;
        const start = Math.max(0, anchor - BUFFER_BEHIND);
        const end = Math.min(images.length - 1, anchor + BUFFER_AHEAD);

        // Evict refs outside the window to bound memory
        const toEvict: number[] = [];
        preloadedRef.current.forEach((_, idx) => {
            if (idx < start || idx > end) toEvict.push(idx);
        });
        if (toEvict.length > 0) {
            setBufferedIndices(prev => {
                const next = new Set(prev);
                toEvict.forEach(idx => {
                    preloadedRef.current.delete(idx);
                    next.delete(idx);
                });
                bufferedIndicesRef.current = next;
                return next;
            });
        }

        // Load the window a few frames at a time instead of all at once
        let cancelled = false;
        let nextIdx = start;
        const ownedIndices = new Set<number>();

        const loadNext = () => {
            if (cancelled) return;
            while (nextIdx <= end && preloadedRef.current.has(nextIdx)) nextIdx++;
            if (nextIdx > end) return;
            const idx = nextIdx++;
            const img = new Image();
            img.onload = () => {
                if (!cancelled) {
                    setBufferedIndices(prev => {
                        if (prev.has(idx)) return prev;
                        const next = new Set(prev);
                        next.add(idx);
                        bufferedIndicesRef.current = next;
                        return next;
                    });
                }
                loadNext();
            };
            img.onerror = img.onload as typeof img.onerror;
            img.src = buildCachedImageUrl(images[idx], cacheAuth);
            preloadedRef.current.set(idx, img);
            ownedIndices.add(idx);
        };

        for (let k = 0; k < BUFFER_CONCURRENCY; k++) loadNext();

        return () => {
            cancelled = true;
            ownedIndices.forEach(idx => {
                if (bufferedIndicesRef.current.has(idx)) return;
                const img = preloadedRef.current.get(idx);
                if (!img) return;
                img.onload = null;
                img.onerror = null;
                img.src = '';
                preloadedRef.current.delete(idx);
            });
        };
    }, [isOpen, reelsMode, preloadWindow, images, cacheAuth, gcsPrefix]);

    // Reels: playback loop
    useEffect(() => {
        if (playTimerRef.current !== null) {
            clearInterval(playTimerRef.current);
            playTimerRef.current = null;
        }
        if (!isPlaying || !reelsMode || images.length === 0) return;

        playTimerRef.current = window.setInterval(() => {
            setSelectedImageIndex(prev => {
                const next = prev + 1;
                if (next >= images.length) {
                    if (loop) return 0;
                    setIsPlaying(false);
                    return prev;
                }
                // Hold position if the next frame is not buffered yet (buffering)
                if (!bufferedIndicesRef.current.has(next)) return prev;
                return next;
            });
        }, 1000 / fps);

        return () => {
            if (playTimerRef.current !== null) {
                clearInterval(playTimerRef.current);
                playTimerRef.current = null;
            }
        };
    }, [isPlaying, reelsMode, fps, loop, images]);

    // Update scale on window resize
    useEffect(() => {
        const handleResize = () => {
            if (imgRef.current) {
                updateScale();
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Track the image area so the image can be scaled up to fill it, not only down
    useEffect(() => {
        const el = containerRef.current;
        if (!isOpen || !el) return;
        const observer = new ResizeObserver(([entry]) => setAreaSize({ width: entry.contentRect.width, height: entry.contentRect.height }));
        observer.observe(el);
        return () => observer.disconnect();
    }, [isOpen]);

    // Wheel / trackpad zoom around the pointer. A non-passive listener is needed to block page scroll and browser pinch zoom.
    useEffect(() => {
        const el = containerRef.current;
        if (!isOpen || !el) return;
        const onWheel = (e: WheelEvent) => {
            const img = imgRef.current;
            if (!img) return;
            e.preventDefault();
            const rect = img.getBoundingClientRect();
            // Untransformed image origin, from the last rendered transform (the one rect reflects)
            const baseX = rect.left - zoomRef.current.x;
            const baseY = rect.top - zoomRef.current.y;
            const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
            setZoom(prev => {
                const z = Math.min(Math.max(prev.z * Math.exp(-delta * 0.002), 1), 10);
                if (z === 1) return { z: 1, x: 0, y: 0 };
                const localX = (e.clientX - baseX - prev.x) / prev.z;
                const localY = (e.clientY - baseY - prev.y) / prev.z;
                return { z, x: e.clientX - baseX - localX * z, y: e.clientY - baseY - localY * z };
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [isOpen]);

    const aspect = imageDimensions ? imageDimensions.width / imageDimensions.height : null;
    const fittedSize = areaSize && aspect
        ? areaSize.width / areaSize.height > aspect
            ? { width: areaSize.height * aspect, height: areaSize.height }
            : { width: areaSize.width, height: areaSize.width / aspect }
        : null;

    const updateScale = () => {
        if (imgRef.current) {
            const { naturalWidth, clientWidth } = imgRef.current;
            if (naturalWidth > 0) {
                setScale(clientWidth / naturalWidth);
            }
        }
    };

    // Draw the shared curve/input-point overlay onto a canvas sized to the displayed image
    const drawCurves = useCallback(() => {
        const canvas = curveCanvasRef.current;
        const img = imgRef.current;
        if (!canvas || !img) return;
        const w = img.clientWidth, h = img.clientHeight;
        if (!w || !h) return;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, w, h);
        drawCurveOverlay(ctx, (nx, ny) => [nx * w, ny * h], curvePoints, inputPoints);
    }, [curvePoints, inputPoints]);

    useEffect(() => {
        drawCurves();
    }, [drawCurves, selectedImageIndex, scale, loadingImage, fittedSize?.width]);

    const fetchImages = async () => {
        setLoadingList(true);
        setError(null);
        try {
            const response = await apiFetch(`/train/images/list?gcs_prefix=${encodeURIComponent(gcsPrefix)}`);
            if (!response.ok) {
                throw new Error(`Failed to list images: ${response.status}`);
            }
            const data = await response.json();
            setCacheAuth(data.cache_auth || null);
            if (data.images && Array.isArray(data.images)) {
                const sorted = [...data.images].sort((a, b) => {
                    const numA = getFrameNumberFromPath(a) ?? Number.MAX_SAFE_INTEGER;
                    const numB = getFrameNumberFromPath(b) ?? Number.MAX_SAFE_INTEGER;
                    return numA - numB || a.localeCompare(b);
                });
                setImages(sorted);
            } else {
                setImages([]);
            }
        } catch (err: any) {
            setCacheAuth(null);
            setError(err.message);
        } finally {
            setLoadingList(false);
        }
    };

    const fetchAnnotations = async () => {
        if (!annotationPath) return;

        setLoadingAnnotations(true);
        try {
            let response = await apiFetch(`/train/file?gcs_path=${encodeURIComponent(annotationPath)}`);
            if (!response.ok) {
                const fallback = annotationPath.replace('annotation.csv', 'annotation_with_pitch_points.csv');
                response = await apiFetch(`/train/file?gcs_path=${encodeURIComponent(fallback)}`);
            }
            if (!response.ok) {
                console.warn('Failed to fetch annotations');
                return;
            }
            const text = await response.text();
            parseAnnotations(text);
        } catch (err) {
            console.error('Error fetching annotations:', err);
        } finally {
            setLoadingAnnotations(false);
        }
    };

    const fetchLabel = async () => {
        if (!labelPath) return;

        setLoadingAnnotations(true);
        try {
            const response = await apiFetch(`/train/file?gcs_path=${encodeURIComponent(labelPath)}`);
            if (!response.ok) {
                console.warn('Failed to fetch label, falling back to annotation');
                setIsNormalized(false);
                if (annotationPath) fetchAnnotations();
                return;
            }
            const text = await response.text();
            parseLabelCsv(text);
            setIsNormalized(true);
        } catch (err) {
            console.error('Error fetching label:', err);
        } finally {
            setLoadingAnnotations(false);
        }
    };

    const parseLabelCsv = (csvText: string) => {
        const lines = csvText.split('\n');
        const map = new Map<number, AnnotationBox[]>();

        let startIndex = 0;
        if (lines.length > 0 && lines[0].toLowerCase().includes('framenumber')) {
            startIndex = 1;
        }

        for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;

            const parts = line.split(',');
            if (parts.length >= 7) {
                const frameNumber = parseInt(parts[0]);
                const vx = parseFloat(parts[1]);
                const vy = parseFloat(parts[2]);
                const vw = parseFloat(parts[3]);
                const vh = parseFloat(parts[4]);
                const objectId = parseInt(parts[5]);
                const playerId = parseInt(parts[6]);

                if (!isNaN(frameNumber)) {
                    if (!map.has(frameNumber)) {
                        map.set(frameNumber, []);
                    }
                    map.get(frameNumber)!.push({ frameNumber, vx, vy, vw, vh, objectId, playerId });
                }
            }
        }
        setAnnotations(map);
    };

    const fetchFieldKeypoints = async () => {
        const csvPath = gcsPrefix.replace(/\/raw_images\/?$/, '/field_keypoints.csv');
        try {
            const response = await apiFetch(`/train/file?gcs_path=${encodeURIComponent(csvPath)}`);
            if (!response.ok) return;
            const text = await response.text();
            const lines = text.split('\n');
            const map = new Map<number, Map<number, Point[]>>();
            for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (!line) continue;
                const parts = line.split(',');
                if (parts.length >= 4) {
                    const frame = parseInt(parts[0]);
                    const x = parseInt(parts[1]);
                    const y = parseInt(parts[2]);
                    const seg = parseInt(parts[3]);
                    if (!isNaN(frame)) {
                        if (!map.has(frame)) map.set(frame, new Map());
                        const segs = map.get(frame)!;
                        if (!segs.has(seg)) segs.set(seg, []);
                        segs.get(seg)!.push({ x, y });
                    }
                }
            }
            setFieldSegments(map);
        } catch (err) {
            console.warn('No field_keypoints.csv found');
        }
    };

    const fetchSegmentTypes = async () => {
        try {
            const response = await apiFetch(`/train/segment_types?gcs_prefix=${encodeURIComponent(gcsPrefix)}`);
            if (!response.ok) return;
            setSegmentTypes(await response.json());
        } catch (err) {
            console.warn('Failed to fetch segment types');
        }
    };

    const fetchCurveOverlay = async () => {
        try {
            const response = await apiFetch(`/train/curve_overlay?gcs_prefix=${encodeURIComponent(gcsPrefix)}`);
            if (!response.ok) return;
            const data = await response.json();
            setCurvePoints(data.curve_points || null);
            setInputPoints(data.input_points || []);
        } catch (err) {
            console.warn('Failed to fetch curve overlay');
        }
    };

    const parseAnnotations = (csvText: string) => {
        const lines = csvText.split('\n');
        const map = new Map<number, AnnotationBox[]>();
        
        let startIndex = 0;
        if (lines.length > 0 && lines[0].toLowerCase().includes('framenumber')) {
            startIndex = 1;
        }

        for (let i = startIndex; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            
            const parts = line.split(',');
            if (parts.length >= 6) {
                const frameNumber = parseInt(parts[0]);
                const vx = parseFloat(parts[1]);
                const vy = parseFloat(parts[2]);
                const vw = parseFloat(parts[3]);
                const vh = parseFloat(parts[4]);
                const objectId = parseInt(parts[5]);

                if (!isNaN(frameNumber)) {
                    if (!map.has(frameNumber)) {
                        map.set(frameNumber, []);
                    }
                    map.get(frameNumber)!.push({ frameNumber, vx, vy, vw, vh, objectId });
                }
            }
        }
        setAnnotations(map);
    };

    const getColorForId = (id: number) => {
        let h = id;
        h = ((h >> 16) ^ h) * 0x45d9f3b;
        h = ((h >> 16) ^ h) * 0x45d9f3b;
        h = (h >> 16) ^ h;
        const hue = Math.abs(h) % 360;
        const lightness = 40 + (Math.abs(h >> 8) % 20);
        return `hsla(${hue}, 85%, ${lightness}%, 0.8)`;
    };

    const editFrame = images[selectedImageIndex] ? getFrameNumberFromPath(images[selectedImageIndex]) : null;
    const editBoxes = editFrame !== null ? annotations.get(editFrame) || [] : [];
    const selectedIndex = !selected
        ? -1
        : selected.playerId !== undefined
            ? editBoxes.findIndex(b => b.playerId === selected.playerId)
            : selected.frame === editFrame ? selected.index! : -1;

    const selectBox = (sel: Selection | null) => {
        setSelected(sel);
        setEditStatus('');
        if (sel && editFrame !== null) {
            setRangeStart(String(editFrame));
            setRangeEnd(String(editFrame));
        }
    };

    const applyEdit = (result: EditResult, message: string, next?: Selection | null, blockedId: string = targetId) => {
        if (result.conflicts.length > 0) {
            setEditStatus(`Blocked: playerId ${blockedId} already exists in ${result.conflicts.length} frame(s), first at ${result.conflicts[0]}`);
            return;
        }
        setUndoStack(prev => [...prev.slice(-99), annotations]);
        setRedoStack([]);
        setAnnotations(result.map);
        setDirty(true);
        setEditStatus(`${message} (${result.changed} frame(s))`);
        if (next !== undefined) setSelected(next);
    };

    const handleUndo = () => {
        if (undoStack.length === 0) return setEditStatus('Nothing to undo');
        setRedoStack(prev => [annotations, ...prev]);
        setAnnotations(undoStack[undoStack.length - 1]);
        setUndoStack(prev => prev.slice(0, -1));
        setDirty(true);
        setEditStatus(`Undone (${undoStack.length - 1} more to undo)`);
    };

    const handleRedo = () => {
        if (redoStack.length === 0) return setEditStatus('Nothing to redo');
        setUndoStack(prev => [...prev, annotations]);
        setAnnotations(redoStack[0]);
        setRedoStack(prev => prev.slice(1));
        setDirty(true);
        setEditStatus(`Redone (${redoStack.length - 1} more to redo)`);
    };

    const handleChangeFrame = () => {
        const target = parseInt(targetId);
        if (selectedIndex < 0 || editFrame === null || isNaN(target)) {
            setEditStatus('Select a box on this frame and enter a new playerId');
            return;
        }
        applyEdit(setPlayerId(annotations, editFrame, selectedIndex, target), `Set playerId ${target} on frame ${editFrame}`, { playerId: target });
    };

    const handleDeleteFrame = () => {
        if (selectedIndex < 0 || editFrame === null) {
            setEditStatus('Select a box on this frame');
            return;
        }
        applyEdit(deleteBox(annotations, editFrame, selectedIndex), `Deleted box on frame ${editFrame}`, null);
    };

    const handleRangeAction = (kind: 'reassign' | 'exchange' | 'delete' | 'interpolate') => {
        const start = parseInt(rangeStart);
        const end = parseInt(rangeEnd);
        const pid = selected?.playerId;
        if (pid === undefined || isNaN(start) || isNaN(end)) {
            setEditStatus('Range actions need a selected box with a playerId and valid start/end frames');
            return;
        }
        const [lo, hi] = start <= end ? [start, end] : [end, start];
        if (kind === 'delete') {
            applyEdit(deleteRange(annotations, lo, hi, pid), `Deleted ${pid} in ${lo}-${hi}`);
            return;
        }
        if (kind === 'interpolate') {
            const result = interpolateRange(annotations, lo, hi, pid);
            if (result.changed === 0) setEditStatus(`Nothing to fill: ${pid} has no missing frames between two of its boxes in ${lo}-${hi}`);
            else applyEdit(result, `Interpolated ${pid} in ${lo}-${hi}`);
            return;
        }
        const target = parseInt(targetId);
        if (isNaN(target) || target === pid) {
            setEditStatus('Enter a different target playerId');
            return;
        }
        if (kind === 'reassign') {
            applyEdit(reassignRange(annotations, lo, hi, pid, target), `Reassigned ${pid} -> ${target} in ${lo}-${hi}`, { playerId: target });
        } else {
            applyEdit(exchangeRange(annotations, lo, hi, pid, target), `Exchanged ${pid} <-> ${target} in ${lo}-${hi}`);
        }
    };

    const pointerPos = (e: React.MouseEvent) => {
        const rect = imgRef.current!.getBoundingClientRect();
        return {
            x: Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1),
            y: Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1),
        };
    };

    const handlePanStart = (e: React.MouseEvent) => {
        if (drawMode || zoom.z <= 1 || e.button !== 0) return;
        e.preventDefault();
        panRef.current = { cx: e.clientX, cy: e.clientY, x: zoom.x, y: zoom.y, moved: false };
    };

    const handlePanMove = (e: React.MouseEvent) => {
        const pan = panRef.current;
        if (!pan) return;
        const dx = e.clientX - pan.cx;
        const dy = e.clientY - pan.cy;
        if (Math.abs(dx) + Math.abs(dy) > 3) pan.moved = true;
        setZoom(prev => ({ ...prev, x: pan.x + dx, y: pan.y + dy }));
    };

    const handlePanEnd = () => {
        if (!panRef.current) return;
        suppressClickRef.current = panRef.current.moved;
        panRef.current = null;
    };

    // A click that ends a pan drag must not select or deselect boxes
    const consumePanClick = () => {
        const suppress = suppressClickRef.current;
        suppressClickRef.current = false;
        return suppress;
    };

    const handleDrawStart = (e: React.MouseEvent) => {
        if (!drawMode || !imgRef.current) return;
        e.preventDefault();
        const { x, y } = pointerPos(e);
        setDrag({ x0: x, y0: y, x1: x, y1: y });
    };

    const handleDrawMove = (e: React.MouseEvent) => {
        if (!drag) return;
        const { x, y } = pointerPos(e);
        setDrag({ ...drag, x1: x, y1: y });
    };

    const handleDrawEnd = () => {
        if (!drag || editFrame === null || !imgRef.current) return;
        setDrag(null);
        const rect = imgRef.current.getBoundingClientRect();
        const vx = Math.min(drag.x0, drag.x1);
        const vy = Math.min(drag.y0, drag.y1);
        const vw = Math.abs(drag.x1 - drag.x0);
        const vh = Math.abs(drag.y1 - drag.y0);
        if (vw * rect.width < 4 || vh * rect.height < 4) return;

        if (pending) {
            if (pending.frameNumber === editFrame) {
                setEditStatus('Move to another frame to draw the END box');
                return;
            }
            const result = addBoxes(annotations, pending, { ...pending, frameNumber: editFrame, vx, vy, vw, vh });
            const label = pending.playerId ?? 'ball';
            const [lo, hi] = [Math.min(pending.frameNumber, editFrame), Math.max(pending.frameNumber, editFrame)];
            applyEdit(result, `Added ${label} in ${lo}-${hi}`, pending.playerId !== undefined ? { playerId: pending.playerId } : undefined, String(label));
            if (result.conflicts.length === 0) setPending(null);
            return;
        }

        let playerId: number | undefined;
        if (drawObjectId === 0) {
            const input = window.prompt('playerId', String(selected?.playerId ?? ''));
            modalRef.current?.focus();
            if (input === null) return;
            playerId = parseInt(input);
            if (isNaN(playerId)) {
                setEditStatus('Invalid playerId');
                return;
            }
        }
        const box: AnnotationBox = { frameNumber: editFrame, vx, vy, vw, vh, objectId: drawObjectId, playerId };
        const label = playerId ?? 'ball';
        if (drawRange) {
            setPending(box);
            setEditStatus(`START box set for ${label} @ ${editFrame}`);
            return;
        }
        applyEdit(addBoxes(annotations, box), `Added ${label} on frame ${editFrame}`, playerId !== undefined ? { playerId } : undefined, String(label));
    };

    const handleSave = async () => {
        if (!labelPath) return;
        setSaving(true);
        try {
            console.log('[save] refreshing token');
            if (!(await refresh())) throw new Error('session expired, sign in again in another tab and retry');
            const body = JSON.stringify({ gcs_path: labelPath, csv: toLabelCsv(annotations) });
            console.log(`[save] sending ${(body.length / 1e6).toFixed(1)} MB to /train/label`);
            const response = await apiFetch('/train/label', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
                signal: AbortSignal.timeout(120000),
            });
            const text = await response.text();
            console.log(`[save] response ${response.status}: ${text.slice(0, 200)}`);
            if (!response.ok) throw new Error(`HTTP ${response.status} ${text.slice(0, 200)}`);
            const data = JSON.parse(text);
            setDirty(false);
            setEditStatus(`Saved ${data.rows} rows (backup: ${data.backup_path || 'none'})`);
        } catch (err: any) {
            console.error('[save] failed', err);
            setEditStatus(`Save failed: ${err.name === 'TimeoutError' ? 'timed out after 2 minutes' : err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleClose = () => {
        if (dirty && !window.confirm('You have unsaved label edits. Close without saving?')) return;
        onClose();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;

        if ((e.metaKey || e.ctrlKey) && editMode && (e.key.toLowerCase() === 'z' || e.key === 'y')) {
            e.preventDefault();
            if (e.key === 'y' || e.shiftKey) handleRedo();
            else handleUndo();
        } else if (e.key === ' ' && reelsMode) {
            e.preventDefault();
            setIsPlaying(prev => !prev);
        } else if (e.key === 'ArrowRight') {
            setIsPlaying(false);
            setSelectedImageIndex(prev => Math.min(prev + 1, images.length - 1));
        } else if (e.key === 'ArrowLeft') {
            setIsPlaying(false);
            setSelectedImageIndex(prev => Math.max(prev - 1, 0));
        } else if (e.key === 'Escape') {
            if (drawMode || drawArmed) {
                setDrawMode(false);
                setDrawArmed(false);
                setPending(null);
            } else {
                handleClose();
            }
        } else if ((e.key === 'Delete' || e.key === 'Backspace') && editMode && selectedIndex >= 0) {
            e.preventDefault();
            handleDeleteFrame();
        } else if (drawArmed && editMode && (e.key === 'a' || e.key === 'r')) {
            setDrawArmed(false);
            setDrawMode(true);
            setDrawRange(e.key === 'r');
            setPending(null);
        } else if (e.key === 'a' && editMode) {
            setDrawArmed(true);
        }
    };

    const onImageLoad = () => {
        setLoadingImage(false);
        if (imgRef.current) {
            const { naturalWidth, naturalHeight } = imgRef.current;
            setImageDimensions({ width: naturalWidth, height: naturalHeight });
            if (originalMode) {
                setSourceDimensions({ width: naturalWidth, height: naturalHeight });
            }
        }
        updateScale();
        drawCurves();
    };

    const handleJumpTo = () => {
        const index = parseInt(jumpInput);
        if (!isNaN(index) && index >= 1 && index <= images.length) {
            setSelectedImageIndex(index - 1);
            if (modalRef.current) {
                modalRef.current.focus();
            }
        } else {
            setJumpInput((selectedImageIndex + 1).toString());
        }
    };

    const currentImage = images[selectedImageIndex];
    const useOriginal = isOpen && !!currentImage && (originalMode || !cacheAuth);
    const originalImageUrl = useSignedImageUrl(useOriginal ? currentImage : null);

    if (!isOpen) return null;

    const currentImageUrl = useOriginal ? originalImageUrl : currentImage ? buildCachedImageUrl(currentImage, cacheAuth!) : '';
    const currentFrameNumber = currentImage ? getFrameNumberFromPath(currentImage) : null;
    const currentAnnotations = currentFrameNumber !== null ? annotations.get(currentFrameNumber) || [] : [];
    const currentSegments = currentFrameNumber !== null ? fieldSegments.get(currentFrameNumber) : null;
    const coordinateDimensions = sourceDimensions || (originalMode ? imageDimensions : null);
    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 2000,
                color: 'white'
            }}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
            ref={modalRef}
        >
            <div style={{ padding: '4px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#333' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span>{gcsPrefix}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginLeft: '10px' }}>
                        <span>(</span>
                        <input 
                            type="text" 
                            value={jumpInput}
                            onChange={(e) => setJumpInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleJumpTo()}
                            style={{ 
                                width: '50px', 
                                textAlign: 'center',
                                color: 'black',
                                padding: '2px'
                            }}
                        />
                        <button onClick={handleJumpTo} style={{ cursor: 'pointer', padding: '2px 5px', color: 'black' }}>Go</button>
                        <span>/ {images.length})</span>
                    </div>
                    <button
                        onClick={() => {
                            if (!reelsMode) setOriginalMode(false);
                            setReelsMode(prev => !prev);
                        }}
                        disabled={!cacheAuth}
                        style={{ cursor: cacheAuth ? 'pointer' : 'default', padding: '2px 8px', marginLeft: '10px', color: 'black', background: reelsMode ? '#4caf50' : '#ddd' }}
                    >
                        Reels
                    </button>
                    <button
                        onClick={() => {
                            setReelsMode(false);
                            setOriginalMode(prev => !prev);
                        }}
                        disabled={!cacheAuth}
                        style={{ cursor: cacheAuth ? 'pointer' : 'default', padding: '2px 8px', color: 'black', background: originalMode ? '#4caf50' : '#ddd' }}
                    >
                        Original
                    </button>
                    <button
                        onClick={() => {
                            setEditMode(prev => !prev);
                            setSelected(null);
                            setDrawMode(false);
                            setDrawArmed(false);
                            setPending(null);
                        }}
                        disabled={!isNormalized}
                        title={isNormalized ? '' : 'Edit needs label.csv'}
                        style={{ cursor: isNormalized ? 'pointer' : 'default', padding: '2px 8px', color: 'black', background: editMode ? '#ff9800' : '#ddd' }}
                    >
                        Edit
                    </button>
                    {loadingAnnotations && <span> - Loading annotations...</span>}
                    {currentFrameNumber !== null && <span> - Frame: {currentFrameNumber}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {dirty && (
                        <button onClick={handleSave} disabled={saving} style={{ cursor: 'pointer', padding: '5px 10px', color: 'black', background: '#ff9800' }}>
                            {saving ? 'Saving...' : 'Save label.csv'}
                        </button>
                    )}
                    <button onClick={handleClose} style={{ cursor: 'pointer', padding: '5px 10px', color: 'black' }}>Close</button>
                </div>
            </div>

            <div 
                style={{ 
                    flex: 1, 
                    minHeight: 0,
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    overflow: 'hidden', 
                    position: 'relative' 
                }}
                ref={containerRef}
            >
                {loadingList ? (
                    <div>Loading list...</div>
                ) : error ? (
                    <div style={{ color: 'red' }}>Error: {error}</div>
                ) : images.length === 0 ? (
                    <div>No images found in this path.</div>
                ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        {/* Navigation Buttons */}
                        <button 
                            onClick={() => setSelectedImageIndex(prev => Math.max(prev - 1, 0))}
                            disabled={selectedImageIndex === 0}
                            style={{ 
                                position: 'absolute', left: '20px', 
                                fontSize: '24px', 
                                background: 'rgba(0,0,0,0.5)', 
                                color: 'white', 
                                border: 'none', 
                                cursor: 'pointer',
                                padding: '10px',
                                borderRadius: '50%',
                                zIndex: 3
                            }}
                        >
                            &lt;
                        </button>

                        {/* Image Container - uses relative position for absolute children */}
                        <div
                            style={{
                                position: 'relative', display: 'inline-block', maxWidth: '100%', maxHeight: '100%', cursor: drawMode ? 'crosshair' : zoom.z > 1 ? 'grab' : undefined,
                                transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.z})`, transformOrigin: '0 0'
                            }}
                            onClick={() => !consumePanClick() && editMode && !drawMode && selectBox(null)}
                            onMouseDown={(e) => {
                                handlePanStart(e);
                                handleDrawStart(e);
                            }}
                            onMouseMove={(e) => {
                                handlePanMove(e);
                                handleDrawMove(e);
                            }}
                            onMouseUp={() => {
                                handlePanEnd();
                                handleDrawEnd();
                            }}
                            onMouseLeave={() => {
                                panRef.current = null;
                                setDrag(null);
                            }}
                        >
                            {loadingImage && (
                                <div style={{ 
                                    position: 'absolute', top: '50%', left: '50%', 
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 1
                                }}>
                                    Loading image...
                                </div>
                            )}
                            <img 
                                ref={imgRef}
                                src={currentImageUrl || undefined}
                                alt={currentImage}
                                style={{ 
                                    ...(fittedSize ? { width: fittedSize.width, height: fittedSize.height } : { maxWidth: '100%', maxHeight: '100%' }),
                                    opacity: loadingImage ? 0.5 : 1,
                                    transition: 'opacity 0.2s',
                                    display: 'block'
                                }}
                                onLoad={onImageLoad}
                                onError={() => setLoadingImage(false)}
                            />
                            
                            {/* Overlays */}
                            {!loadingImage && imageDimensions && (isNormalized || coordinateDimensions) && currentAnnotations.map((box, idx) => {
                                const hasLabel = box.playerId !== undefined;
                                const colorKey = hasLabel ? box.playerId! : box.objectId;
                                const color = getColorForId(colorKey);
                                const labelText = hasLabel ? String(box.playerId) : `ID: ${box.objectId}`;
                                const dimensions = coordinateDimensions || imageDimensions;

                                const left = isNormalized ? box.vx * 100 : (box.vx / dimensions.width) * 100;
                                const top = isNormalized ? box.vy * 100 : (box.vy / dimensions.height) * 100;
                                const width = isNormalized ? box.vw * 100 : (box.vw / dimensions.width) * 100;
                                const height = isNormalized ? box.vh * 100 : (box.vh / dimensions.height) * 100;

                                const hasPid = hasLabel && !Number.isNaN(box.playerId);
                                const isSelected = !!selected && (selected.playerId !== undefined
                                    ? box.playerId === selected.playerId
                                    : selected.frame === currentFrameNumber && selected.index === idx);
                                const dimmed = editMode && !!selected && !isSelected;

                                return (
                                    <div
                                        key={idx}
                                        onClick={(e) => {
                                            if (!editMode) return;
                                            e.stopPropagation();
                                            if (consumePanClick()) return;
                                            selectBox(isSelected ? null : hasPid ? { playerId: box.playerId } : { frame: currentFrameNumber!, index: idx });
                                        }}
                                        style={{
                                            position: 'absolute',
                                            left: `${left}%`,
                                            top: `${top}%`,
                                            width: `${width}%`,
                                            height: `${height}%`,
                                            border: `${(isSelected ? 3 : 2) / zoom.z}px solid ${color}`,
                                            boxShadow: isSelected ? '0 0 0 2px white' : undefined,
                                            opacity: dimmed ? 0.25 : 1,
                                            boxSizing: 'border-box',
                                            pointerEvents: editMode && !drawMode ? 'auto' : 'none',
                                            cursor: editMode ? 'pointer' : undefined,
                                            zIndex: isSelected ? 3 : 2
                                        }}
                                    >
                                        <span style={{ 
                                            position: 'absolute', 
                                            top: '-20px', 
                                            left: '0', 
                                            backgroundColor: color, 
                                            color: 'white', 
                                            padding: '2px 4px', 
                                            fontSize: '10px',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {labelText}
                                        </span>
                                    </div>
                                );
                            })}

                            {pending && pending.frameNumber === currentFrameNumber && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: `${pending.vx * 100}%`,
                                        top: `${pending.vy * 100}%`,
                                        width: `${pending.vw * 100}%`,
                                        height: `${pending.vh * 100}%`,
                                        border: `${2 / zoom.z}px dashed #ffd180`,
                                        boxSizing: 'border-box',
                                        pointerEvents: 'none',
                                        zIndex: 4
                                    }}
                                />
                            )}

                            {drag && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: `${Math.min(drag.x0, drag.x1) * 100}%`,
                                        top: `${Math.min(drag.y0, drag.y1) * 100}%`,
                                        width: `${Math.abs(drag.x1 - drag.x0) * 100}%`,
                                        height: `${Math.abs(drag.y1 - drag.y0) * 100}%`,
                                        border: `${2 / zoom.z}px dashed white`,
                                        boxSizing: 'border-box',
                                        pointerEvents: 'none',
                                        zIndex: 4
                                    }}
                                />
                            )}

                            {/* Field Segments */}
                            {!loadingImage && coordinateDimensions && currentSegments && currentSegments.size > 0 && (
                                <svg
                                    viewBox={`0 0 100 100`}
                                    preserveAspectRatio="none"
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }}
                                >
                                    {Array.from(currentSegments.entries()).map(([segId, pts]) => {
                                        if (pts.length < 2) return null;
                                        const color = segmentColor(segId);
                                        const type = segmentTypes[String(segId)] || 'line';
                                        const w = coordinateDimensions.width;
                                        const h = coordinateDimensions.height;
                                        const mid = pts[Math.floor(pts.length / 2)];
                                        const ell = type === 'arc_closed' ? fitEllipse(pts, w, h) : null;

                                        return (
                                            <g key={segId}>
                                                {ell ? (
                                                    <ellipse
                                                        cx={ell.cx} cy={ell.cy} rx={ell.rx} ry={ell.ry}
                                                        transform={`rotate(${ell.angle} ${ell.cx} ${ell.cy})`}
                                                        fill="none"
                                                        stroke={color}
                                                        strokeWidth="0.3"
                                                        opacity="0.9"
                                                    />
                                                ) : type === 'arc' ? (
                                                    <path
                                                        d={catmullRomToPath(pts, w, h)}
                                                        fill="none"
                                                        stroke={color}
                                                        strokeWidth="0.3"
                                                        opacity="0.9"
                                                    />
                                                ) : (
                                                    <polyline
                                                        points={pts.map(p => `${(p.x / w) * 100},${(p.y / h) * 100}`).join(' ')}
                                                        fill="none"
                                                        stroke={color}
                                                        strokeWidth="0.3"
                                                        opacity="0.9"
                                                    />
                                                )}
                                                {pts.map((p, i) => (
                                                    <circle
                                                        key={i}
                                                        cx={`${(p.x / w) * 100}`}
                                                        cy={`${(p.y / h) * 100}`}
                                                        r="0.4"
                                                        fill={color}
                                                        stroke="white"
                                                        strokeWidth="0.1"
                                                    />
                                                ))}
                                                <text
                                                    x={`${(mid.x / w) * 100}`}
                                                    y={`${(mid.y / h) * 100 - 1}`}
                                                    fill={color}
                                                    fontSize="1.5"
                                                    textAnchor="middle"
                                                    stroke="black"
                                                    strokeWidth="0.15"
                                                    paintOrder="stroke"
                                                >
                                                    {segId}
                                                </text>
                                            </g>
                                        );
                                    })}
                                </svg>
                            )}

                            {/* Curve grid + input points overlay (panorama curves datasets) */}
                            <canvas
                                ref={curveCanvasRef}
                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }}
                            />
                        </div>

                        <button 
                            onClick={() => setSelectedImageIndex(prev => Math.min(prev + 1, images.length - 1))}
                            disabled={selectedImageIndex === images.length - 1}
                            style={{ 
                                position: 'absolute', right: '20px', 
                                fontSize: '24px', 
                                background: 'rgba(0,0,0,0.5)', 
                                color: 'white', 
                                border: 'none', 
                                cursor: 'pointer',
                                padding: '10px',
                                borderRadius: '50%',
                                zIndex: 3
                            }}
                        >
                            &gt;
                        </button>
                        
                        <div style={{ position: 'absolute', bottom: '10px', background: 'rgba(0,0,0,0.5)', padding: '5px', borderRadius: '4px', zIndex: 3 }}>
                            {currentImage}
                        </div>
                    </div>
                )}
            </div>

            {editMode && (
                <div style={{ padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', background: '#3a2f1f' }}>
                    <button
                        onClick={() => {
                            setDrawMode(prev => !prev);
                            setPending(null);
                        }}
                        style={{ cursor: 'pointer', padding: '2px 8px', color: 'black', background: drawMode ? '#ff9800' : '#ddd' }}
                    >
                        Draw
                    </button>
                    <select value={drawObjectId} onChange={(e) => setDrawObjectId(Number(e.target.value))} style={{ color: 'black', padding: '2px' }}>
                        <option value={0}>Player</option>
                        <option value={1}>Ball</option>
                    </select>
                    <select
                        value={drawRange ? 'range' : 'single'}
                        onChange={(e) => {
                            setDrawRange(e.target.value === 'range');
                            setPending(null);
                        }}
                        style={{ color: 'black', padding: '2px' }}
                    >
                        <option value="single">Single frame</option>
                        <option value="range">Range</option>
                    </select>
                    {drawArmed && <span style={{ color: '#ffd180' }}>Draw: press <b>a</b> for single frame, <b>r</b> for range</span>}
                    {drawMode && drawRange && (
                        <span style={{ color: '#ffd180' }}>
                            {pending
                                ? <>Draw <b>END</b> box for <b>{pending.playerId ?? 'ball'}</b> (START @ {pending.frameNumber})</>
                                : <>Draw <b>START</b> box</>}
                        </span>
                    )}
                    {pending && <button onClick={() => setPending(null)} style={{ cursor: 'pointer', padding: '2px 8px', color: 'black' }}>Cancel range</button>}
                    <button onClick={handleUndo} disabled={undoStack.length === 0} style={{ cursor: 'pointer', padding: '2px 8px', color: 'black' }}>Undo ({undoStack.length})</button>
                    <button onClick={handleRedo} disabled={redoStack.length === 0} style={{ cursor: 'pointer', padding: '2px 8px', color: 'black' }}>Redo ({redoStack.length})</button>
                    {!selected ? (
                        <span>Click a box to select it</span>
                    ) : (
                        <>
                            <span>
                                Selected: <b>{selected.playerId !== undefined ? selected.playerId : 'no playerId'}</b>
                                {selectedIndex < 0 && ' (not in this frame)'}
                            </span>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                New playerId
                                <input value={targetId} onChange={(e) => setTargetId(e.target.value)} style={{ width: '80px', color: 'black', padding: '2px' }} />
                            </label>
                            <span>This frame:</span>
                            <button onClick={handleChangeFrame} style={{ cursor: 'pointer', padding: '2px 8px', color: 'black' }}>Change</button>
                            <button onClick={handleDeleteFrame} style={{ cursor: 'pointer', padding: '2px 8px', color: 'black' }}>Delete</button>
                            <span style={{ marginLeft: '10px' }}>Range:</span>
                            <input value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} style={{ width: '70px', color: 'black', padding: '2px' }} />
                            <span>to</span>
                            <input value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} style={{ width: '70px', color: 'black', padding: '2px' }} />
                            <button onClick={() => handleRangeAction('reassign')} disabled={selected.playerId === undefined} style={{ cursor: 'pointer', padding: '2px 8px', color: 'black' }}>Reassign to new</button>
                            <button onClick={() => handleRangeAction('exchange')} disabled={selected.playerId === undefined} style={{ cursor: 'pointer', padding: '2px 8px', color: 'black' }}>Exchange with new</button>
                            <button onClick={() => handleRangeAction('delete')} disabled={selected.playerId === undefined} style={{ cursor: 'pointer', padding: '2px 8px', color: 'black' }}>Delete in range</button>
                            <button onClick={() => handleRangeAction('interpolate')} disabled={selected.playerId === undefined} style={{ cursor: 'pointer', padding: '2px 8px', color: 'black' }}>Interpolate in range</button>
                        </>
                    )}
                    {editStatus && <span style={{ color: editStatus.startsWith('Blocked') || editStatus.startsWith('Save failed') ? '#ff8a80' : '#b9f6ca' }}>{editStatus}</span>}
                </div>
            )}

            {reelsMode && (
                <div style={{ padding: '4px 10px', display: 'flex', alignItems: 'center', gap: '15px', background: '#333' }}>
                    <button onClick={() => setIsPlaying(prev => !prev)} style={{ cursor: 'pointer', padding: '5px 15px', color: 'black' }}>
                        {isPlaying ? 'Pause' : 'Play'}
                    </button>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        FPS
                        <input
                            type="number"
                            min={1}
                            max={30}
                            value={fps}
                            onChange={(e) => setFps(Math.min(30, Math.max(1, parseInt(e.target.value) || 1)))}
                            style={{ width: '50px', textAlign: 'center', color: 'black', padding: '2px' }}
                        />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} />
                        Loop
                    </label>
                    <span>Buffered: {bufferedIndices.size} / {images.length}</span>
                </div>
            )}

            <div style={{ padding: '2px 10px', fontSize: '12px', color: '#aaa', background: '#222' }}>
                Scroll / pinch: zoom at pointer · Drag: move zoomed image · ← / →: prev / next frame · Space: play / pause (Reels) · a a: draw single box, a r: draw range (Edit) · Delete: delete selected box (Edit) · ⌘/Ctrl+Z: undo, ⌘/Ctrl+Shift+Z: redo (Edit) · Esc: exit draw, then close
            </div>
        </div>
    );
};

export default ImageViewer;